/**
 * Unified mobile autoplay handler
 * Handles mobile-specific video autoplay requirements
 * 
 * CRITICAL: Mobile browsers require play() to be called synchronously during user interaction
 */

import { isMobileDeviceSync } from '@/lib/utils/mobile-detection';

/**
 * Play video with mobile autoplay support
 * On mobile: calls play() immediately (required for autoplay)
 * On desktop: can defer with requestIdleCallback for better performance
 */
export function playVideoWithMobileSupport(
  video: HTMLVideoElement,
  onError?: (error: Error) => void,
  forceImmediate: boolean = false
): void {
  const isMobile = isMobileDeviceSync();
  const windowWidth = typeof window !== 'undefined' ? window.innerWidth : 0;

  // FIX: Always play immediately - requestIdleCallback deferral on desktop caused race conditions
  // where the "autoplay blocked" fallback timer fired before the deferred play() completed
  if (video && !video.paused) return; // Already playing
  
  video.play().then(() => {
  }).catch((error) => {
    if (onError) onError(error);
  });
}

/**
 * Try to play video immediately (for mobile autoplay)
 * Returns promise that resolves when play() completes
 */
export async function tryPlayVideoImmediate(
  video: HTMLVideoElement
): Promise<boolean> {
  if (!video || !video.paused) return true; // Already playing
  
  try {
    await video.play();
    return true;
  } catch (error) {
    // Autoplay blocked or other error
    return false;
  }
}

/**
 * Setup video element for mobile autoplay
 * Ensures all required attributes are set
 */
export function setupVideoForMobileAutoplay(video: HTMLVideoElement): void {
  video.muted = true;
  video.playsInline = true;
  video.setAttribute('muted', 'true');
  video.setAttribute('playsinline', 'true');
  video.setAttribute('webkit-playsinline', 'true');
  video.autoplay = true;
  video.loop = true;
}

/**
 * Check if video autoplay is blocked
 * Returns true if video is paused but should be playing
 */
export function isAutoplayBlocked(video: HTMLVideoElement): boolean {
  return video.paused && video.autoplay && video.muted && document.visibilityState === 'visible';
}
